import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {useLayoutEffect} from "react"
import {IS_APPLE} from "../../utils/environment"
import {lexicalInsertLink} from "../../actions/insert-link"
import {$getSelection, $isRangeSelection, $isRootOrShadowRoot, FORMAT_TEXT_COMMAND} from "lexical"
import {getSelectedNode} from "../../utils/getSelectedNode"
import {$isLinkNode} from "@lexical/link"
import {lexicalChangeHeading} from "../../actions/change-heading"
import {$isHeadingNode, HeadingTagType} from "@lexical/rich-text"
import {lexicalChangeList} from "../../actions/change-list"
import {$isListNode, ListNode} from "@lexical/list"
import {$findMatchingParent, $getNearestNodeOfType} from "@lexical/utils"
import {blockTypeToBlockName} from "../toolbar/toolbar"

type Props = {
    onKeyDown?          : {(event: KeyboardEvent):void}
    onEscape?           : {(event: KeyboardEvent):void}
    onCommandEnterKey?  : {(event: KeyboardEvent):void}
}

const debug = false
// const debug = "CommandsPlugin"

export default function CommandsPlugin(props: Props) {
    const [editor] = useLexicalComposerContext()

    const toggleList = (type: 'number' | 'bullet') => {
        editor.update(()=>{
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
                const anchorNode = selection.anchor.getNode();
                let element =
                    anchorNode.getKey() === 'root'
                        ? anchorNode
                        : $findMatchingParent(anchorNode, (e) => {
                            const parent = e.getParent()
                            return parent !== null && $isRootOrShadowRoot(parent);
                        });

                if (element === null) {
                    element = anchorNode.getTopLevelElementOrThrow()
                }

                let currentBockType
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(
                        anchorNode,
                        ListNode,
                    )
                    currentBockType = parentList
                        ? parentList.getListType()
                        : element.getListType()
                } else {
                    const type = $isHeadingNode(element)
                        ? element.getTag()
                        : element.getType()
                    if (type in blockTypeToBlockName) {
                        currentBockType = type as keyof typeof blockTypeToBlockName
                    }
                }

                debug && console.log(debug, 'textCommand {0} list currentBockType'.f(type), currentBockType)
                currentBockType && lexicalChangeList(editor, currentBockType!, type)
            }
        })
    }

    useLayoutEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const shortcut = []
            if (IS_APPLE ? event.metaKey : event.ctrlKey)   shortcut.push('ctrl')
            if (event.altKey)                               shortcut.push('alt')
            if (event.shiftKey)                             shortcut.push('shift')
            shortcut.push(event.code)

            const shortcutValue = shortcut.join('+')

            debug && console.log(debug, 'onKeyDown', shortcutValue, event)

            switch (shortcutValue) {
                case 'ctrl+KeyK':
                    debug && console.log(debug, 'insertLink', event)
                    event.preventDefault()
                    lexicalInsertLink(editor)
                    break

                case 'alt+Enter':
                    debug && console.log(debug, 'openLink', event)
                    event.preventDefault()
                    event.stopPropagation()

                    editor.update(()=>{
                        const selection = $getSelection()
                        if ($isRangeSelection(selection)) {
                            const node = getSelectedNode(selection)
                            const parent = node.getParent()
                            let url
                            if ($isLinkNode(parent)) {
                                url = parent.getURL()
                            } else if ($isLinkNode(node)) {
                                url = node.getURL()
                            }

                            debug && console.log(debug, 'openLink url', url)
                            window.open(url, '_blank')
                        }
                    })
                    break

                case 'ctrl+KeyJ':
                    debug && console.log(debug, 'textCommand code', event)
                    event.preventDefault()

                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
                    break

                case 'ctrl+shift+KeyX':
                    debug && console.log(debug, 'textCommand strikethrough', event)
                    event.preventDefault()

                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
                    break

                case 'ctrl+Period':
                    debug && console.log(debug, 'textCommand superscript', event)
                    event.preventDefault()

                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')
                    break

                case 'ctrl+Comma':
                    debug && console.log(debug, 'textCommand subscript', event)
                    event.preventDefault()

                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')
                    break

                case 'ctrl+alt+Digit0':
                    debug && console.log(debug, 'textCommand normal text style', event)
                    event.preventDefault()

                    lexicalChangeHeading(editor, 'paragraph' as HeadingTagType)
                    break

                case 'ctrl+alt+Digit1':
                    debug && console.log(debug, 'textCommand heading 1', event)
                    event.preventDefault()

                    lexicalChangeHeading(editor, 'h1')
                    break

                case 'ctrl+alt+Digit2':
                    debug && console.log(debug, 'textCommand heading 2', event)
                    event.preventDefault()

                    lexicalChangeHeading(editor, 'h2')
                    break

                case 'ctrl+alt+Digit3':
                    debug && console.log(debug, 'textCommand heading 3', event)
                    event.preventDefault()

                    lexicalChangeHeading(editor, 'h3')
                    break

                case 'ctrl+shift+Digit7':
                    debug && console.log(debug, 'textCommand number list', event)
                    event.preventDefault()

                    toggleList('number')
                    break

                case 'ctrl+shift+Digit8':
                    debug && console.log(debug, 'textCommand bullet list', event)
                    event.preventDefault()

                    toggleList('bullet')
                    break

                case 'Escape':
                    props.onEscape && props.onEscape(event)
                    break

                case 'ctrl+Enter':
                    props.onCommandEnterKey && props.onCommandEnterKey(event)
                    break
            }

            props.onKeyDown && props.onKeyDown(event)
        }

        return editor.registerRootListener((
            rootElement     : null | HTMLElement,
            prevRootElement : null | HTMLElement,
        ) => {
            if (prevRootElement !== null) {
                prevRootElement.removeEventListener('keydown', onKeyDown, true);
            }
            if (rootElement !== null) {
                rootElement.addEventListener('keydown', onKeyDown, true);
            }
        })
    }, [editor])

    return null
}
